import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import queryString from 'query-string';

import { OidcContext, OidcDisplay } from '../../../../types';
import { LoginType } from '../../../../utilities/social-login';
import { isOidcDisplayMode } from '../../../../utilities/oidc/oidc';
import {
  EventAttributes,
  useAnalyticsClient,
} from '../../../../utilities/analytics/analytics-web-client';
import { useRetryAccountCreation } from '../hooks/useRetryAccountCreation';
import { CREATE_ACCOUNT_CONFIRMATION_PAGE_ID as pageId } from '../constants';
import { LoginLinkWrapper } from './styled';
import ButtonLink from '../../../../components/ButtonLink';

export const WhiteboxFooter = ({
  loginType,
  createNeeded,
  isEmbedded,
  oidcContext,
  verifyNeeded,
}: {
  loginType: LoginType;
  createNeeded: boolean;
  isEmbedded: boolean;
  oidcContext: OidcContext | undefined;
  verifyNeeded: boolean;
}) => {
  const showLoginLink =
    createNeeded && !isEmbedded && !isOidcDisplayMode(oidcContext, OidcDisplay.Signup);

  const analyticsClient = useAnalyticsClient();

  const handleOnLoginLinkClick = useCallback(async () => {
    const eventAttributes: EventAttributes = {
      loginType: loginType,
      confirmationCreateAccountNeeded: createNeeded,
      confirmationVerifyEmailNeeded: verifyNeeded,
    };
    await analyticsClient.linkClickedEvent(
      pageId,
      'alreadyHaveAccountCreateAccountIdp',
      eventAttributes
    );
  }, [loginType, createNeeded, verifyNeeded, analyticsClient]);

  const { resendVerificationCode } = useRetryAccountCreation();

  const stripAuth0StateToken = useCallback((search: string): string => {
    const params = queryString.parse(search);
    delete params.state;
    return Object.keys(params).length ? '?' + queryString.stringify(params) : '';
  }, []);

  // TODO: encapsulate in our hook, to ensure the auth0 state token is stripped?
  const loginLink = '/login' + stripAuth0StateToken(location.search);

  if (showLoginLink || verifyNeeded) {
    return (
      <>
        {verifyNeeded && (
          <ul>
            <li>
              <ButtonLink
                id="didnt-get-an-email-start-again"
                spacing="none"
                className="link-color-override"
                onClick={resendVerificationCode}>
                <FormattedMessage
                  id="accountcreationconfirmation.resend.verification.code"
                  defaultMessage="Resend verification code"
                  description="Link button to resend the verification code if the user did not receive an email"
                />
              </ButtonLink>
            </li>
          </ul>
        )}

        {showLoginLink && (
          <LoginLinkWrapper addTopMargin={verifyNeeded}>
            <Link id="already-have-an-account" to={loginLink} onClick={handleOnLoginLinkClick}>
              <FormattedMessage
                id="accountcreationconfirmation.already.have.account.log.in"
                defaultMessage="Already have an Atlassian account? Log in"
              />
            </Link>
          </LoginLinkWrapper>
        )}
      </>
    );
  }

  return null;
};
